import { Breakpoint } from "@omni/types/enums/breakPoint.enum";
import { createMayBeForOptional, MaybeType } from "@omni/utils/maybe.utils";
import { cn } from "@omni/utils/styles";
import React, { HTMLAttributes, Ref } from "react";

import { TAG_ZONE_ID } from "@/utils/tagging/tag.utils";

import {
    COL_DEFAULT_CLASS,
    ColOffset,
    ColSize,
    generateColClasses,
    generateOffsetClasses
} from "./col.variants";

const colWidths: Breakpoint[] = [
    Breakpoint.xs,
    Breakpoint.sm,
    Breakpoint.md,
    Breakpoint.lg,
    Breakpoint.xl
];

type ColumnPropsObject = {
    size?: ColSize;
    offset?: ColOffset;
};

export type ColumnProps = ColSize | ColumnPropsObject;

export type ColProps = HTMLAttributes<HTMLDivElement> & {
    children?: React.ReactNode;
    className?: string;
    xs?: ColumnProps;
    sm?: ColumnProps;
    md?: ColumnProps;
    lg?: ColumnProps;
    xl?: ColumnProps;
    ref?: Ref<HTMLDivElement>;
};

const Col = ({ ref, children, className, ...attributes }: ColProps) => {
    const colClasses = [];

    colWidths.forEach((colWidth) => {
        const columnProp = attributes[colWidth];

        if (!columnProp && columnProp !== "") {
            return;
        }

        const isMayBeColumnPropsObject =
            createMayBeForOptional<ColumnPropsObject>(
                ["size", "offset"],
                columnProp
            );

        if (isMayBeColumnPropsObject.type === MaybeType.Just) {
            const columnProps = isMayBeColumnPropsObject.value;
            const colClass = generateColClasses(colWidth, columnProps.size);

            const offsetClass = generateOffsetClasses(
                colWidth,
                columnProps.offset
            );

            colClasses.push(cn(colClass, offsetClass));
        } else {
            const colClass = generateColClasses(
                colWidth,
                columnProp as ColSize
            );
            colClasses.push(colClass);
        }
    });

    if (!colClasses.length) {
        colClasses.push(generateColClasses());
    }

    const classes = cn(COL_DEFAULT_CLASS, colClasses, className);
    const csProp = {
        [TAG_ZONE_ID]: (attributes as any)[TAG_ZONE_ID]
    };

    return (
        <div
            ref={ref}
            className={cn(classes)}
            {...csProp}
        >
            {children}
        </div>
    );
};

export { Col };
